/**
 * Analytics entry point. Import `analytics` and call `.track()` / `.page()` / `.identify()` —
 * everything else in this folder is plumbing behind that one object.
 *
 * Multi-provider by design: this app currently registers Google Analytics (GA4) when
 * `VITE_GA_MEASUREMENT_ID` is set, plus a console provider in development. Adding a second
 * production provider (PostHog, Plausible, Mixpanel, ...) is one line —
 *
 *   analytics.register(new SomeOtherProvider({ ... }));
 *
 * — implementing `AnalyticsProvider` from ./types. No call site anywhere in the app changes.
 *
 * Nothing here sends anything until consent is granted: see lib/consent.tsx, which calls
 * `analytics.setEnabled(true)` once the visitor accepts. Registering a provider before that
 * point queues it up but does not load its script or set any cookie.
 */
import { AnalyticsManager } from "./manager";
import { ConsoleAnalyticsProvider } from "./providers/consoleProvider";
import { GoogleAnalyticsProvider } from "./providers/googleAnalytics";

export const analytics = new AnalyticsManager();

const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
if (gaMeasurementId) {
  analytics.register(new GoogleAnalyticsProvider({ measurementId: gaMeasurementId }));
}

// Development-only: every tracked event is mirrored to the console so instrumentation can be
// verified without a real measurement id, and dev traffic never reaches production analytics.
if (import.meta.env.DEV) {
  analytics.register(new ConsoleAnalyticsProvider());
}

export { AnalyticsEvent, type AnalyticsEventName } from "./events";
export type { AnalyticsEventProperties, AnalyticsProvider, AnalyticsUserTraits } from "./types";
